<template>
    <div>
        <div class="sending">
            <div class="sending-tabs" :class="{'sending-tabs_single': isSingleTab}">
                <button
                        v-for="tab in tabs"
                        :key="tab.name"
                        :class="['tab-button',
                        currentTab && (tab.pieceType === currentTab.pieceType ? 'active' : '')]"
                        @click="setPieceType(tab)"
                >
                    <span
                            class="tab-icon"
                            :style="`background-image: url(${tab.img});`">
                    </span>
                    {{ tab.name | t }}
                </button>
            </div>
            <component v-if="currentTab !== null"
                       :is="currentTab.component"
                       :piece="piece"
                       :isDomestic="isDomestic"
                       @error="onError"
                       @hintClick="onHintClick"
                       class="tab"
            ></component>

            <div class="sending-buttons">
                <button @click.prevent="onCancelClick"
                        class="button-white">
                    {{ texts.cancel }}
                </button>
                <button
                        @click.prevent="onSaveClick"
                        :disabled="isError"
                        class="button">
                    {{ texts.save }}
                </button>
            </div>
        </div>
        <modal name="hint"
               :height='`auto`'
               :width='`874px`'
               :scrollable='true'>
            <piece-hint/>
        </modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import PIECE_TYPES from '@/models/pieceTypes';
import pieceGoodImg from '@/assets/icons/piece-good.png';
import pieceDocumentImg from '@/assets/icons/piece-document.png';
import { spinner } from '@/components/core';
import pieceHint from './piece-hint/piece-hint.component.vue';
import PieceService from '../piece.service';

const pieceDoc = () => ({
  component: import('./piece-items/piece-doc.component.vue'),
  loading: spinner,
  delay: 200,
  timeout: 3000,
});

const pieceItem = () => ({
  component: import('./piece-items/piece-item.component.vue'),
  loading: spinner,
  delay: 200,
  timeout: 3000,
});

export default {
  name: 'piece-edit',
  pieceService: new PieceService(),
  props: [
    'editablePiece',
  ],
  components: {
    pieceHint,
  },
  data() {
    return {
      tabs: [],
      piece: null,
      error: false,
    };
  },
  mounted() {
    this.setPiece();
    this.tabs.push({
      name: 'Document',
      pieceType: PIECE_TYPES.Documents,
      img: pieceDocumentImg,
      component: pieceDoc,
    });
    if (this.isSenderRu && (!this.isContryNotDeliveryCargo)) {
      this.tabs.push({
        name: 'Item',
        pieceType: PIECE_TYPES.NonDocuments,
        img: pieceGoodImg,
        component: pieceItem,
      });
    }
  },
  computed: {
    ...mapState('preparation', [
      'locationFrom',
      'locationTo',
    ]),
    isSingleTab() {
      return this.tabs.length === 1;
    },
    currentTab() {
      if (this.piece) {
        return this.tabs[this.piece.type.type];
      }
      return null;
    },
    isError() {
      return this.error;
    },
    isDomestic() {
      if (this.$store.state.preparation.locationTo) {
        return this.$store.state.preparation.locationTo.CountryCode === 'RU';
      }
      return true;
    },
    isContryNotDeliveryCargo() {
      const countryCode = this.$store.state.preparation.locationTo.CountryCode
      if (countryCode) {
        return countryCode === 'KZ' || countryCode === 'AM' || countryCode === 'KG';
      }
      return false;
    },
    isSenderRu() {
      return this.locationFrom.CountryCode === 'RU';
    },
    isRecipientRu() {
      return this.locationTo.CountryCode === 'RU';
    },
    texts() {
      return {
        sendingWhat: this.$t('SendingWhat'),
        save: this.$t('Save'),
        cancel: this.$t('Cancel'),
        hint: this.$t('Hint'),
      };
    },
  },
  watch: {
    editablePiece() {
      this.setPiece();
    },
  },
  methods: {
    setPiece() {
      const { width, height, depth } = this.editablePiece.dimensions;
      this.piece = this.$options.pieceService.getPiece(
        this.$options.pieceService.getPieceType(this.editablePiece.type.type),
        this.$options.pieceService.getPieceDimensions(width, height, depth),
        +this.editablePiece.weight,
      );
      if (this.editablePiece.id !== null && this.editablePiece.id !== undefined) {
        this.piece.id = this.editablePiece.id;
      }
    },
    setPieceType(tab) {
      this.piece.type = this.$options.pieceService.getPieceType(tab.pieceType);
      this.piece.dimensions = this.$options.pieceService.getPieceDimensions();
    },
    onCancelClick() {
      this.$emit('cancel');
    },
    async onSaveClick() {
      this.$emit('save', this.piece);
    },
    onError(error) {
      this.error = error;
    },
    onHintClick() {
      this.$modal.show('hint', {
        height: 'auto',
        scrollable: true,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.tab-button {
    position: relative;
    flex: 1 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: -1px;
    padding: 0 5px 18px;
    background-color: transparent;
    border: 0;
    border-bottom: 1px solid #bebebe;
    cursor: pointer;
    color: #000000;
    font-size: 18px;
    opacity: .5;

    &.active {
        opacity: 1;

        &:after {
            background-color: #d40411;
        }
    }

    &:after {
        position: absolute;
        bottom: -1px;
        left: 0;
        display: block;
        content: "";
        height: 2px;
        width: 100%;
        transition: background-color .25s;
    }

    &:hover:after {
        background-color: #ffcc01;
    }

    @media screen and (max-width: 640px) {
        font-size: 15px;
    }
}

.tab-icon {
    display: inline-block;
    width: 50px;
    height: 50px;
    margin-right: 18px;
    background-repeat: no-repeat;
    background-position: 100% 50%;
    background-size: contain;

    @media screen and (max-width: 640px) {
        width: 26px;
        height: 26px;
        margin-right: 12px;
    }
}

.tab {
    max-width: 396px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 34px;
    padding-top: 40px;

    @media screen and (max-width: 640px) {
        margin-bottom: 8px;
        padding-top: 28px;
        padding-left: 20px;
        padding-right: 20px;
    }
}

.sending {
    margin-top: 3px;
    margin-bottom: 24px;
    padding: 22px 38px 36px;
    border-radius: 4px;
    background-color: #f5f5f5;

    @media screen and (max-width: 769px) {
        padding-left: 0;
        padding-right: 0;
        padding-bottom: 16px;
    }
}

.sending-tabs {
    display: flex;

    &_single {
        max-width: 395px;
        margin: auto;
    }
}

.sending-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 396px;
    margin-left: auto;
    margin-right: auto;

    button {
        width: 144px;
        margin-left: 5px;
        margin-right: 5px;
        max-width: calc(50% - 10px);
    }

    @media screen and (max-width: 640px) {
        padding-left: 20px;
        padding-right: 20px;
    }
}

.hint-label {
    display: flex;
    max-width: 396px;
    margin-left: auto;
    margin-right: auto;
}
</style>
